import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import {
  validateArrayMinMaxLength,
  validatePhone,
} from "../../common/validators";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const formVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export default function ContactModal({
  show,
  phoneDDD,
  setPhoneDDD,
  phoneNumber,
  setPhoneNumber,
  closeModal,
}) {
  const theme = useTheme();
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isDDDValid, setIsDDDValid] = useState(false);

  const handleDDD = (e) => {
    const value = e.target.value;
    setIsDDDValid(validateArrayMinMaxLength(value, 2, 2));
    setPhoneDDD(value);
  };

  const handlePhone = (e) => {
    const value = e.target.value;
    setIsPhoneValid(validatePhone(value));
    setPhoneNumber(value);
  };

  const isFormValid = isDDDValid && isPhoneValid;

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="40%"
      maxWidth="480px"
    >
      <ModalHeader
        title="Contato"
        icon={ContactPhoneIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <motion.div
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.info.main, 0.06),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 18, color: theme.palette.info.main, flexShrink: 0 }}
              />
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Dados usados apenas internamente para contato sobre integrações
                logísticas.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <motion.div variants={itemVariants}>
                <TextField
                  id="phoneddd"
                  size="small"
                  label="DDD"
                  variant="filled"
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  error={phoneDDD.length > 0 && !isDDDValid}
                  helperText={
                    phoneDDD.length > 0 && !isDDDValid ? "DDD inválido" : ""
                  }
                  value={phoneDDD}
                  onChange={handleDDD}
                />
              </motion.div>
            </Grid>

            <Grid item xs={8}>
              <motion.div variants={itemVariants}>
                <TextField
                  id="phone-number"
                  size="small"
                  label="Telefone"
                  variant="filled"
                  fullWidth
                  inputProps={{ maxLength: 9 }}
                  error={phoneNumber.length > 0 && !isPhoneValid}
                  helperText={
                    phoneNumber.length > 0 && !isPhoneValid
                      ? "Telefone inválido"
                      : ""
                  }
                  value={phoneNumber}
                  onChange={handlePhone}
                />
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </ModalContent>

      <ModalFooter>
        <Button
          variant="outlined"
          onClick={closeModal}
          sx={{
            mr: 1.5,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            borderColor: theme.palette.grey[300],
            "&:hover": {
              borderColor: theme.palette.grey[400],
              bgcolor: theme.palette.grey[50],
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!isFormValid}
          onClick={closeModal}
          startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            bgcolor: theme.palette.primary.main,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&.Mui-disabled": {
              bgcolor: theme.palette.grey[200],
              color: theme.palette.grey[400],
            },
          }}
        >
          Confirmar
        </Button>
      </ModalFooter>
    </AnimatedModal>
  );
}
